import React, { useEffect, useMemo, useState } from 'react';
/**
 * SpellingPickRenderer — SPR specialist grapheme-choice cloze.
 *
 * A Dutch word is shown with a highlighted gap where the spelling-rule target
 * letters belong (before + [gap] + after). The learner taps the correct
 * grapheme tile to fill each gap. Productive (the rule must be applied) yet
 * fast and playful (tap, not type) — the gap-filler between fill_in and
 * full-word recognition for recall-heavy SPR families.
 *
 * Contract (QuestionRendererProps):
 *  - reports ready via onReadyChange once every gap is filled
 *  - emits the answer (JSON array of chosen graphemes, word-aligned) on the
 *    rising edge of shouldValidate
 */
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { resolveSpellingPickGraphemes, type SpellingPickConfig } from '../../../content/types';
import { soundManager } from '../../../utils/soundManager';
import { hapticManager } from '../../../utils/hapticManager';
const VARIANT_LABEL: Record<SpellingPickConfig['variant'], string> = {
  'ei-ij': 'ei of ij?',
  'au-ou': 'au of ou?',
  'd-t': 'd of t?',
  custom: 'Kies de juiste letters'
};
export function SpellingPickRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.spellingPick as SpellingPickConfig | undefined;
  const words = config?.words ?? [];
  const graphemes = useMemo(() => config ? resolveSpellingPickGraphemes(config) : [] as string[], [config]);
  // selections[wordIndex] = chosen grapheme (or null when still open)
  const [selections, setSelections] = useState<(string | null)[]>(() => words.map(() => null));
  // Reset when the question changes.
  useEffect(() => {
    setSelections(words.map(() => null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);
  const allFilled = words.length > 0 && selections.every(s => s !== null && s !== undefined);
  useEffect(() => {
    onReadyChange?.(allFilled);
  }, [allFilled, onReadyChange]);
  // Edge-triggered submit — payload is a word-aligned JSON array of picks.
  const answerPayload = useMemo(() => JSON.stringify(selections.map(s => s ?? '')), [selections]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handlePick = (wordIndex: number, grapheme: string) => {
    setSelections(prev => {
      const next = [...prev];
      next[wordIndex] = grapheme;
      return next;
    });
    soundManager.playClick();
    hapticManager.hapticSelection();
  };
  if (!config || words.length === 0 || graphemes.length === 0) {
    return <div className="p-4 bg-amber-50 rounded-xl text-amber-800 font-semibold text-center" data-id="element-12001" data-mp="1k9q1i2alxlxr">
        Deze vraag mist gegevens om te tonen.
      </div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-xl mx-auto" data-id="element-12002" data-mp="3vvqtn79nsga6">
      {/* Variant chip — instant "what am I doing" cue (P6). */}
      <div className="flex justify-center mb-5" data-id="element-12003" data-mp="3qy8h76wfnixf">
        <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold tracking-wide" style={{
        backgroundColor: hexToRgba(color, 0.12),
        color
      }} data-id="element-12004" data-mp="ihnjim994c9m">
          {config.promptLabel || VARIANT_LABEL[config.variant]}
        </span>
      </div>

      <ul className="flex flex-col gap-3" role="list" data-id="element-12005" data-mp="dhrcee59c4pu">
        {words.map((word, wi) => {
        const chosen = selections[wi];
        return <li key={`${question.id}-w${wi}`} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 shadow-sm" data-id="element-12006" data-mp="2y9bdq9wvrqoq" data-mpi={wi}>
              {/* The word with its gap */}
              <div className="flex-1 flex items-center justify-center sm:justify-start flex-wrap text-2xl sm:text-3xl font-extrabold text-slate-800 select-none" data-id="element-12007" data-mp="39o4xkfko5r9m">
                {word.before && <span data-id="element-12008" data-mp="2zsl0hpudcmja">{word.before}</span>}
                <AnimatePresence mode="wait" initial={false} data-id="element-12009" data-mp="2zpp2y2mfnzvd">
                  {chosen ? <motion.span key={`filled-${chosen}`} initial={{
                scale: 0.6,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0.6,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 24
              }} className="mx-0.5 px-1 rounded-md" style={{
                color,
                backgroundColor: hexToRgba(color, 0.1)
              }} data-id="element-12010" data-mp="17tj8zo11tij">
                      {chosen}
                    </motion.span> : <motion.span key="gap" initial={{
                opacity: 0.5
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="inline-block mx-1 align-middle" data-id="element-12011" data-mp="167rl3vzlfi4b">
                      <span className="inline-block w-10 sm:w-12 border-b-4 border-dashed rounded-sm" style={{
                  borderColor: hexToRgba(color, 0.5),
                  height: '0.15em'
                }} aria-hidden="true" data-id="element-12012" data-mp="3h2yr6pg0wi6c" />
                    </motion.span>}
                </AnimatePresence>
                {word.after && <span data-id="element-12013" data-mp="x60oewcwqro6">{word.after}</span>}
              </div>

              {/* Grapheme tiles for this word */}
              <div className="flex items-center justify-center gap-2" role="group" aria-label={`Kies de letters voor woord ${wi + 1}`} data-id="element-12014" data-mp="1c31gj0z0gon6">
                {graphemes.map(g => {
              const isSelected = chosen === g;
              return <motion.button key={g} type="button" onClick={() => handlePick(wi, g)} whileTap={{
                scale: 0.9
              }} whileHover={{
                scale: 1.05
              }} aria-pressed={isSelected} className="min-w-[3rem] px-3 py-2 rounded-xl text-xl font-extrabold border-2 transition-colors" style={isSelected ? {
                backgroundColor: color,
                borderColor: color,
                color: '#fff'
              } : {
                backgroundColor: '#fff',
                borderColor: '#e2e8f0',
                color: '#334155'
              }} data-id="element-12015" data-mp="1gx5rrwfb7iu2">
                      {g}
                    </motion.button>;
            })}
              </div>
            </li>;
      })}
      </ul>

      <p className="text-center text-slate-400 text-sm mt-6 font-medium" data-id="element-12016" data-mp="1bvloyveb09n8">
        {allFilled ? 'Klaar? Klik op Controleren!' : words.length > 1 ? 'Tik bij elk woord de juiste letters aan' : 'Tik de juiste letters aan'}
      </p>
    </motion.div>;
}
SpellingPickRenderer.displayName = 'SpellingPickRenderer';